import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CampaignCarouselService } from 'app/core/services/campaign-carousel.service';
import { CampaignCarouselSlide } from 'app/shared/models/campaign';

/** Attributes that keep the opened tab from reaching back into the app. */
const EXTERNAL_LINK_FEATURES = 'noopener,noreferrer';
const EXTERNAL_LINK_TARGET = '_blank';

/** How long a slide stays on screen before the carousel advances. */
const AUTO_ADVANCE_INTERVAL_MS = 7000;

/** Media query used to honour the operating system's reduced-motion setting. */
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Each slide fills the viewport, so the track shifts by one full width. */
const SLIDE_WIDTH_PERCENT = 100;

const TAB_INDEX_FOCUSABLE = 0;
const TAB_INDEX_SKIPPED = -1;

/**
 * BCP-47 tag to format the campaign date with, per UI language.
 *
 * @remarks
 * `Intl` is used instead of Angular's `date` pipe because the pipe needs the
 * locale registered under the exact id it is handed, and `app.module.ts` registers
 * English as `en-US` — passing the `en` that `TranslateService` reports would throw
 * at runtime. `Intl` resolves the tag itself and needs no registration.
 */
const DATE_LOCALES: Record<string, string> = {
  ptBR: 'pt-BR',
  en: 'en-US',
  es: 'es-ES',
  it: 'it-IT',
};

const FALLBACK_DATE_LOCALE = DATE_LOCALES['ptBR'];

/** Long form, matching the design: "14 de agosto de 2026". */
const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
};

/**
 * Campaign carousel rendered at the bottom of the dashboard.
 *
 * @remarks
 * The same slides for every user, on every visit — there is no per-user state,
 * no dismissal and no "already seen" tracking, so the component never writes
 * anything back. It replaces the login announcement modal, which interrupted the
 * user once and then hid the campaign forever.
 *
 * Deliberately hand-rolled instead of using `NgbCarousel`: the globally loaded
 * stylesheet is Bootstrap 3, whose `.carousel` rules expect `.item` children,
 * while ng-bootstrap renders Bootstrap 5 markup. Its slides would come out
 * unstyled and partially fought over by the Bootstrap 3 rules.
 */
@Component({
  selector: 'app-campaign-carousel',
  imports: [CommonModule, TranslateModule],
  templateUrl: './campaign-carousel.component.html',
  styleUrls: ['./campaign-carousel.component.scss'],
})
export class CampaignCarouselComponent implements OnInit, OnDestroy {
  private readonly carouselService = inject(CampaignCarouselService);
  private readonly translateService = inject(TranslateService);

  private readonly destroy$ = new Subject<void>();

  /** Handle of the auto-advance timer; `null` whenever it is not running. */
  private autoAdvanceHandle: ReturnType<typeof setInterval> | null = null;

  /**
   * Announces whether the carousel ended up with anything to render.
   *
   * @remarks
   * The carousel hides itself when there is nothing to show, which leaves a hole
   * in the page the host may want to fill. Emitting lets the host decide — the
   * dashboard swaps in the mobile app promo — without fetching the campaigns a
   * second time just to find out.
   *
   * Emitted once the request settles, including when it fails: a failure degrades
   * to no slides, and the host should treat that the same as an empty carousel.
   */
  @Output() readonly hasCampaigns = new EventEmitter<boolean>();

  slides: CampaignCarouselSlide[] = [];
  activeIndex = 0;

  ngOnInit(): void {
    this.loadSlides();
  }

  ngOnDestroy(): void {
    this.stopAutoAdvance();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Tells whether the carousel has anything to render. */
  get hasSlides(): boolean {
    return this.slides.length > 0;
  }

  /** Tells whether the navigation controls are worth showing. */
  get hasMultipleSlides(): boolean {
    return this.slides.length > 1;
  }

  /** Offset that brings the active slide into the viewport. */
  get trackTransform(): string {
    return `translateX(-${this.activeIndex * SLIDE_WIDTH_PERCENT}%)`;
  }

  /** Tells whether a slide is the one currently on screen. */
  isActive(index: number): boolean {
    return index === this.activeIndex;
  }

  /**
   * Keeps the off-screen slides out of the tab order.
   *
   * @remarks
   * Every slide stays in the DOM so the track can animate between them, so
   * without this the CTA of a slide the user cannot see would still be
   * focusable — a keyboard trap in empty space.
   */
  slideTabIndex(index: number): number {
    if (this.isActive(index)) {
      return TAB_INDEX_FOCUSABLE;
    }

    return TAB_INDEX_SKIPPED;
  }

  /**
   * Formats a slide's publication date for the active language.
   *
   * @param slide - Slide whose `createdAt` is to be rendered.
   * @returns The long-form date, or an empty string when the value is unusable.
   *
   * @remarks
   * Returns empty rather than throwing or printing "Invalid Date": the date is
   * supporting metadata, and a campaign with a malformed timestamp should still
   * show its artwork and its call to action.
   */
  formatDate(slide: CampaignCarouselSlide): string {
    if (!slide.createdAt) {
      return '';
    }

    const parsed = new Date(slide.createdAt);

    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(
      this.resolveDateLocale(),
      DATE_FORMAT,
    ).format(parsed);
  }

  /** Maps the UI language onto a formatting locale, falling back to ptBR. */
  private resolveDateLocale(): string {
    const language = this.translateService.currentLang;

    if (language && DATE_LOCALES[language]) {
      return DATE_LOCALES[language];
    }

    return FALLBACK_DATE_LOCALE;
  }

  /** Moves to the previous slide, wrapping around at the first one. */
  previous(): void {
    if (!this.hasMultipleSlides) {
      return;
    }

    this.activeIndex =
      (this.activeIndex - 1 + this.slides.length) % this.slides.length;
    this.restartAutoAdvance();
  }

  /** Moves to the next slide, wrapping around at the last one. */
  next(): void {
    if (!this.hasMultipleSlides) {
      return;
    }

    this.activeIndex = (this.activeIndex + 1) % this.slides.length;
    this.restartAutoAdvance();
  }

  /** Jumps straight to a slide, as the indicator dots do. */
  goTo(index: number): void {
    if (index < 0 || index >= this.slides.length) {
      return;
    }

    this.activeIndex = index;
    this.restartAutoAdvance();
  }

  /**
   * Suspends the rotation while the user is reading or tabbing through a slide.
   *
   * @remarks
   * Bound to pointer *and* focus events: a keyboard user who reaches the CTA
   * should not have the slide swapped out from under them.
   */
  pauseAutoAdvance(): void {
    this.stopAutoAdvance();
  }

  /** Resumes the rotation once the pointer or focus leaves the carousel. */
  resumeAutoAdvance(): void {
    this.startAutoAdvance();
  }

  /** Opens the campaign in a new tab. */
  openSlide(slide: CampaignCarouselSlide): void {
    window.open(slide.url, EXTERNAL_LINK_TARGET, EXTERNAL_LINK_FEATURES);
  }

  private loadSlides(): void {
    this.carouselService
      .fetchCampaigns()
      .pipe(takeUntil(this.destroy$))
      .subscribe((slides) => {
        this.slides = slides;
        this.activeIndex = 0;
        this.restartAutoAdvance();
        this.hasCampaigns.emit(this.hasSlides);
      });
  }

  private restartAutoAdvance(): void {
    this.stopAutoAdvance();
    this.startAutoAdvance();
  }

  private startAutoAdvance(): void {
    if (this.autoAdvanceHandle !== null) {
      return;
    }

    if (!this.hasMultipleSlides) {
      return;
    }

    if (this.prefersReducedMotion()) {
      return;
    }

    this.autoAdvanceHandle = setInterval(() => {
      this.activeIndex = (this.activeIndex + 1) % this.slides.length;
    }, AUTO_ADVANCE_INTERVAL_MS);
  }

  private stopAutoAdvance(): void {
    if (this.autoAdvanceHandle === null) {
      return;
    }

    clearInterval(this.autoAdvanceHandle);
    this.autoAdvanceHandle = null;
  }

  /**
   * Tells whether the user asked the system to avoid motion.
   *
   * @remarks
   * Guarded because `matchMedia` is absent in the jsdom-based test environment
   * and in any non-browser rendering target.
   */
  private prefersReducedMotion(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }

    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  }
}
